import { KeyboardEvent, useCallback, useRef } from 'react'
import Badge from '@mui/material/Badge'
import cn from 'classnames'
import { formatBadgeCount } from '../../helpers/helpers'
import { NotesPanelTab } from '../../helpers/notesPanel'
import Icon from '../Common/Icon'
import IconButton from '../Common/IconButton'
import styles from './NotesRail.module.scss'
import { renderTabIcon } from './tabIcons'

interface IProps {
  /** The tabs this page offers, in rail order. The enum value doubles as the label. */
  tabs: NotesPanelTab[]
  newFlagsCount: number
  /**
   * Notes on this page the user hasn't read — null while the count is in flight.
   * Unread rather than total, so the badge reads like a notification count and
   * settles to nothing once the user has looked, instead of always showing a
   * number nobody can act on.
   */
  unreadCount: number | null
  /** Notes that are action-required and unresolved. Only resolving clears this. */
  needsActionCount: number
  /** Open the docked panel on the given tab; isQuickAdd also focuses the composer. */
  onOpen: (tab: NotesPanelTab, isQuickAdd?: boolean) => void
}

const NotesRail = ({ tabs, newFlagsCount, unreadCount, needsActionCount, onOpen }: IProps) => {
  const railRef = useRef<HTMLDivElement>(null)
  const hasNotesTab = tabs.includes(NotesPanelTab.notes)

  // The rail is a toolbar, so it behaves like one: arrows move between buttons.
  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
      return
    }
    const buttons = Array.from(railRef.current?.querySelectorAll<HTMLButtonElement>('button') || [])
    if (buttons.length === 0) {
      return
    }
    event.preventDefault()
    const currentIndex = buttons.indexOf(document.activeElement as HTMLButtonElement)
    const offset = event.key === 'ArrowDown' ? 1 : -1
    const nextIndex = (currentIndex + offset + buttons.length) % buttons.length
    buttons[nextIndex].focus()
  }, [])

  const handleQuickAdd = useCallback(() => onOpen(NotesPanelTab.notes, true), [onOpen])

  const getBadgeContent = useCallback(
    (tab: NotesPanelTab): string | null => {
      if (tab === NotesPanelTab.flags) {
        return newFlagsCount > 0 ? formatBadgeCount(newFlagsCount) : null
      }
      // No flash-of-zero: while the count loads there is simply no badge, and with
      // nothing unread the "+" carries the invitation on its own.
      if (tab === NotesPanelTab.notes) {
        return unreadCount && unreadCount > 0 ? formatBadgeCount(unreadCount) : null
      }
      return null
    },
    [newFlagsCount, unreadCount],
  )

  return (
    <div
      ref={railRef}
      className={styles.rail}
      data-cy="notes-rail"
      role="toolbar"
      aria-orientation="vertical"
      aria-label="Notes"
      onKeyDown={handleKeyDown}
    >
      {hasNotesTab && (
        <IconButton
          tooltipPlacement="left"
          title="Add note"
          className={styles.addButton}
          aria-label="Add note"
          data-cy="notes-rail-add"
          onClick={handleQuickAdd}
        >
          <Icon name="plus" size="sm" />
        </IconButton>
      )}

      {/* Only a separator when there is something on both sides of it. Pages
          without a Notes tab (home, client management) have no quick-add, and an
          unpaired rule reads as a rendering glitch. */}
      {hasNotesTab && tabs.length > 0 && <div className={styles.divider} />}

      {tabs.map((tab) => {
        const badgeContent = getBadgeContent(tab)
        // The needs-action dot rides the Notes icon and is driven purely by
        // unresolved state, so opening the panel never makes it go away.
        const hasNeedsAction = tab === NotesPanelTab.notes && needsActionCount > 0
        const countLabel = badgeContent ? `${tab} (${badgeContent})` : String(tab)
        const label = hasNeedsAction ? `${countLabel}, ${needsActionCount} need action` : countLabel

        return (
          <IconButton
            tooltipPlacement="left"
            title={label}
            className={styles.railButton}
            aria-label={label}
            data-cy={`notes-rail-${tab.toLowerCase()}`}
            onClick={() => onOpen(tab)}
          >
            <Badge
              classes={{
                // Flags are an alarm (red); a note count is just information.
                badge: cn(styles.railBadge, {
                  [styles.railBadgeError]: tab === NotesPanelTab.flags,
                  // Unread is "new", so it gets the accent rather than the
                  // neutral treatment a plain total would.
                  [styles.railBadgeAccent]: tab === NotesPanelTab.notes,
                }),
              }}
              badgeContent={badgeContent}
              invisible={!badgeContent}
            >
              {renderTabIcon(tab)}
            </Badge>
            {hasNeedsAction && <span className={styles.needsActionDot} aria-hidden />}
          </IconButton>
        )
      })}
    </div>
  )
}

export default NotesRail
